/**
 * Feature flags — all OFF by default. A flag governs a whole surface, not a
 * single component: hosts check the flag to decide whether to mount the gated
 * UI at all, so flag-off is byte-for-byte the existing path.
 *
 * `multiCurrency` (FE-N6 / DR-004) gates the entire multi-currency surface.
 * Go-live waits on the backend schema (B1) — but the UI ships dark now,
 * rendered from mock FX fixtures behind this flag. Dev override:
 * `VITE_FLAG_MULTI_CURRENCY=true`.
 *
 * The Clarity v4.0 product-redesign surfaces (Part C) ship the same way —
 * "dark" behind a flag, on mock fixtures, until their backend artifact lands
 * (B6–B8). Flag-off is byte-for-byte the existing path:
 *   - `taxConfidence` (C1 / W1) → TaxConfidenceHero; go-live waits on B6
 *     (`TaxConfidenceResponse`) + the real `TaxProjectionService` formula.
 *   - `review` (C2 / W3) → the keyboard-first `/review` queue; go-live waits
 *     on B7 (`POST /matcher/correction`).
 *   - `onboardingHousehold` (C3 / W4) → the household onboarding rework; the
 *     payoff estimate stays a LABELLED placeholder until the real formula.
 *   - `progressiveNav` (C4 / W2·W5) → the milestone-gated sidebar curation.
 *   - `household` (C4) → the Household sharing surface that replaces the
 *     `HouseholdSection` coming-soon placeholder; go-live waits on B8.
 * Dev overrides: `VITE_FLAG_TAX_CONFIDENCE`, `VITE_FLAG_REVIEW`,
 * `VITE_FLAG_ONBOARDING_HOUSEHOLD`, `VITE_FLAG_PROGRESSIVE_NAV`,
 * `VITE_FLAG_HOUSEHOLD`.
 */

export interface FeatureFlags {
  multiCurrency: boolean;
  /** C1 · W1 Tax-confidence hero. Go-live waits on B6 + the real tax formula. */
  taxConfidence: boolean;
  /** C2 · W3 keyboard-first Review queue (`/review`). Go-live waits on B7. */
  review: boolean;
  /** C3 · W4 household onboarding rework. Estimate stays a labelled placeholder. */
  onboardingHousehold: boolean;
  /** C4 · W2/W5 progressive nav (milestone-gated sidebar curation). */
  progressiveNav: boolean;
  /** C4 · Household sharing surface (replaces the placeholder). Go-live waits on B8. */
  household: boolean;
  /** B3 · Accountant management UI (invite with scoped grants). Backend is live. */
  accountants: boolean;
}

const env = import.meta.env as Record<string, string | undefined>;

function envFlag(key: string): boolean {
  return env[key] === 'true';
}

export const flags: FeatureFlags = {
  multiCurrency: envFlag('VITE_FLAG_MULTI_CURRENCY'),
  taxConfidence: envFlag('VITE_FLAG_TAX_CONFIDENCE'),
  review: envFlag('VITE_FLAG_REVIEW'),
  onboardingHousehold: envFlag('VITE_FLAG_ONBOARDING_HOUSEHOLD'),
  progressiveNav: envFlag('VITE_FLAG_PROGRESSIVE_NAV'),
  household: envFlag('VITE_FLAG_HOUSEHOLD'),
  accountants: envFlag('VITE_FLAG_ACCOUNTANTS'),
};

/**
 * Whether to render the multi-currency surface for a given amount. Hosts call
 * this before mounting `MoneyMC`: the flag must be on AND the amount must be
 * foreign (a base-currency amount always collapses to a plain number).
 */
export function shouldShowMultiCurrency(currency: string, base: string): boolean {
  return flags.multiCurrency && currency !== base;
}
